import {useBreakPoint, useModal} from '@hooks';
import {getAllConnectedUsers, useAppSelector, useHomeBuildingId} from '@lib/store';
import {LocalDateString} from '@lib/store/src/data/timeslots/apiTypes';
import {Avatar} from '@molecules';
import {useMemo} from 'react';
import {UserProps} from 'src/components/molecules/Avatar/types';
import styled from 'styled-components';
import {useConnectionsSchedule} from '@lib/store/src/ui/hooks/timeslots/useConnectionsSchedule';
import {StyledModalPage} from '../../bookWorkspace/BookWorkspaceCard/styles';
import {Tile} from '../Tile';
import {NoConnections, NoOneThere} from './EmptyState';
import {ConnectionOverview_timeslots} from './ConnectionsOverview_timeslots';
import {listConnectionsWithAtLeastOneAppearanceInBuilding} from '@lib/store/src/data/timeslots/viewmodel-helpers/listConnectionsWithAtLeastOneAppearanceInBuilding';

export const ConnectionsTile_timeslots = ({date: dateKey}: {date: LocalDateString}) => {
  const breakpoint = useBreakPoint();
  const {openModal, closeModal, setModalPages} = useModal();
  const buildingId = useHomeBuildingId(dateKey);

  const connectionsSchedule = useConnectionsSchedule(dateKey);

  const isLoading = connectionsSchedule.status === 'pending';
  const allConnections = useAppSelector(getAllConnectedUsers);

  const connectionsInHomeBuilding = useMemo(() => {
    return listConnectionsWithAtLeastOneAppearanceInBuilding(allConnections, connectionsSchedule.workdays, buildingId);
  }, [allConnections, connectionsSchedule.workdays, buildingId]);

  const status = isLoading
    ? 'Loading'
    : allConnections.length === 0
    ? 'UserHasNoConnections'
    : connectionsInHomeBuilding.length === 0
    ? 'NoOneThere'
    : 'Done';

  const onViewAll = () => {
    setModalPages([
      <StyledModalPage
        title="Connections at the office"
        onClose={closeModal}>
        <div className="p-4">
          <ConnectionOverview_timeslots dateKey={dateKey} />
        </div>
      </StyledModalPage>,
    ]);
    openModal();
  };

  if (breakpoint !== 'small') return <></>;

  return (
    <Tile
      title="Connections at the office"
      viewAllAction={status === 'Done' ? onViewAll : undefined}>
      <ol className="overflow-x-scroll flex gap-3 w-full">
        {status === 'Loading' ? (
          <ConnectionSkeletons count={5} />
        ) : status === 'UserHasNoConnections' ? (
          <NoConnections />
        ) : status === 'NoOneThere' ? (
          <NoOneThere />
        ) : (
          connectionsInHomeBuilding.map((c) => (
            <ConnectionBadge
              key={c.id}
              user={c}
            />
          ))
        )}
      </ol>
    </Tile>
  );
};

const ConnectionBadge = ({user}: {user: UserProps}) => {
  return (
    <div
      className="flex flex-col gap-1 w-20 items-center text-center p-4 rounded-md"
      title={user.name}>
      <Avatar
        shouldShowCheckIn={
          true /* Note: Should we not show check-in status if we're looking at tomorrow's reservations? */
        }
        user={user}
        size={'xlarge'}
        className="size-16"
      />
    </div>
  );
};

const ConnectionSkeletons = ({count}: {count: number}) =>
  Array.from({length: count}, (_, i) => ({delay: i * 200})).map(({delay}) => (
    <ConnectionSkeleton
      key={delay}
      delay={delay}
    />
  ));

const ConnectionSkeleton = ({delay}: {delay: number}) => {
  return (
    <div className="flex flex-col gap-1 w-20 items-center p-4">
      <DelayedPulseBlock
        $delay={delay}
        className="bg-slate-100 animate-pulse rounded-full size-16 mb-2"
      />
    </div>
  );
};

const DelayedPulseBlock = styled.div<{$delay: number}>`
  // Note: using styled component here because struggling with tailwind dynamic class names
  animation-delay: ${({$delay}) => `${$delay}ms !important`};
  transition-delay: ${({$delay}) => `${$delay}ms !important`};
`;
